import { useState } from "react";

import { styled } from "@mui/material/styles";
import { Box, Stepper, Step, StepLabel, Typography, Fade } from "@mui/material";
import { useFormik, FormikProvider } from "formik";
import Page from "src/components/Page";
import Logo from "src/components/Logo";
import { onboardingValidationSchema } from "src/util/validationSchemas";
import AboutYouStep from "./AboutYouStep";
import VerifyEmailStep from "./VerifyEmailStep";
import { onboardingService } from "src/services";
import { useDispatch } from "react-redux";
import { setOnboardingData } from "src/store/onboarding/onboarding.slice";


import "src/pages/pages.css";
import ThemeModeToggle from "src/components/ThemeModeToggle";

const RootStyle = styled(Page)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  backgroundColor: "#f6f7ff",
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

// "Your Needs" step has been removed — onboarding is now About You -> Verify Email.
const STEPS = ["About You", "Verify Email"];
const TOTAL_STEPS = STEPS.length;

// TEMPORARY: the send-verification-email endpoint doesn't exist yet. Treat
// every call as successful (response === true) so the UI flow can be built
// and tested end-to-end. Replace this with the real API call once it's
// ready — the call sites (onSubmit below, and Resend in VerifyEmailStep)
// won't need to change, only this implementation.
async function sendVerificationEmail(email) {
  // TODO: replace with real API call, e.g.:
  // const response = await api.post("/auth/send-verification-email", { email });
  // return response.success;
  return Promise.resolve(true);
}

export default function Signup() {
  const [activeStep, setActiveStep] = useState(0);
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      gst: "",
      country: "",
      companyName: "",
     
    },
    validationSchema: onboardingValidationSchema,
    onSubmit: () => {},
  });

 const submitRegistration = async () => {
  formik.setSubmitting(true);
  formik.setStatus(undefined);

  const {
    firstName,
    lastName,
    companyName,
    email,
    phoneNumber,
    gst,
    country,
  } = formik.values;

  const payload = {
    companyInfo: {
      firstName,
      lastName,
      email,
      country,
      phoneNumber,
      gst,
      name: companyName,
    
    },
  };

  try {
    const response = await onboardingService.submitOnboarding(payload);

    dispatch(
      setOnboardingData({
        userId: response?.data?.userId,
        firstName,
        lastName,
        email,
        phoneNumber,
        gst,
        country,
        companyName,
      })
    );

    setActiveStep(1);
  } catch (error) {
    console.error("Onboarding error:", error);
    formik.setStatus(
      error?.response?.data?.message ??
        "Something went wrong sending the verification email. Please try again."
    );
  } finally {
    formik.setSubmitting(false);
  }
};

const validateStepFields = async (fields) => {
  try {
    await onboardingValidationSchema.pick(fields).validate(formik.values, {
      abortEarly: false,
    });
    return true;
  } catch (err) {
    if (err.inner) {
      const errors = {};
      const touchedFields = {};
      err.inner.forEach((e) => {
        errors[e.path] = e.message;
        touchedFields[e.path] = true;
      });
      formik.setErrors({ ...formik.errors, ...errors });
      formik.setTouched({ ...formik.touched, ...touchedFields });
    } else {
      // Not a per-field yup ValidationError — something in the schema
      // itself is throwing. Log it and surface *something* so this
      // never fails silently again.
      console.error("Unexpected validation error:", err);
      formik.setStatus("Something went wrong validating the form. Please try again.");
    }
    return false;
  }
};

// About You is now the only form step, so this validates everything and
// submits registration directly (replaces the old handleAboutYouNext +
// handleYourNeedsSubmit pair).
const handleAboutYouSubmit = async () => {
 

  const fields = [
    "firstName",
    "lastName",
    "email",
    "country",
    "phoneNumber",
    "companyName",
  ];

  if (await validateStepFields(fields)) {
    submitRegistration();
  }
};

  return (
    <RootStyle title="Onboarding | Smart-CMS">
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: { xs: "40px 16px", md: "60px 32px" },
        }}
      >
        <Box
  sx={{
    position: "absolute",
    top: { xs: 16, md: 24 },
    right: { xs: 16, md: 24 },
    zIndex: 10,
  }}
>
  <ThemeModeToggle />
</Box>
        <Box
          sx={{
            width: "100%",
            maxWidth: 560,
            backgroundColor: "background.paper",
            borderRadius: "15px",
            boxShadow: "0 30px 70px rgba(34, 45, 90, 0.12)",
            p: { xs: 3, md: 4 },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Logo />
          </Box>

          {activeStep < TOTAL_STEPS - 1 && (
            <Box sx={{ mb: 4 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              <Typography
                variant="caption"
                color="text.secondary"
                align="center"
                sx={{ display: "block", mt: 1 }}
              >
                Step {activeStep + 1} of {TOTAL_STEPS}
              </Typography>
            </Box>
          )}

          <Fade in timeout={300} key={activeStep}>
            <Box>
              {activeStep === 0 && (
                <FormikProvider value={formik}>
                  <AboutYouStep onSubmit={handleAboutYouSubmit} />
                </FormikProvider>
              )}
              {activeStep === 1 && (
                <VerifyEmailStep
                  email={formik.values.email}
                  onResend={sendVerificationEmail}
                  onEditEmail={() => setActiveStep(0)}
                />
              )}
            </Box>
          </Fade>
        </Box>
      </Box>
    </RootStyle>
  );
}